import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Button, Drawer, Modal } from '@ui/index'
import { IconClearClose } from '@img/icons'
import { createAvatarFolder, deleteCatalogAvatar, uploadCatalogAvatarWithRetry, useAvatarCatalog } from '@api/operatorAvatars'
import type { AvatarCatalogItem } from '@api/operatorAvatars'
import { useProfile } from '@api/profile/state'
import { getAvatarCatalogItemUrl } from '@services/operator-avatar'
import { reportUnexpectedClientError } from '@services/client-error.service'
import { AvatarFileDropzone } from './file-dropzone'
import css from './ui.module.scss'

const MAX_FILE_BYTES = 5 * 1024 * 1024
const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])

export const AvatarCatalogManager = ({isOpen, onClose, onChanged}: {
    isOpen: boolean; onClose: () => void; onChanged?: () => void
}) => {
    const {items, folders, canManage, canEdit, canDelete, isLoading, error: catalogError, loadCatalog} = useAvatarCatalog()
    const [folderID, setFolderID] = useState(1)
    const [files, setFiles] = useState<File[]>([])
    const [previews, setPreviews] = useState<string[]>([])
    const [newFolder, setNewFolder] = useState(false)
    const [folderName, setFolderName] = useState('')
    const [deleteTarget, setDeleteTarget] = useState<AvatarCatalogItem | null>(null)
    const [replacementID, setReplacementID] = useState<number | undefined>()
    const [deleteError, setDeleteError] = useState('')
    const [busy, setBusy] = useState(false)
    const [progress, setProgress] = useState<number | null>(null)
    const [uploadPosition, setUploadPosition] = useState('')
    const [retryWaitSeconds, setRetryWaitSeconds] = useState<number | null>(null)
    const [stopping, setStopping] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const active = useRef(false)
    const operation = useRef(0)
    const pending = useRef(false)
    const controller = useRef<AbortController>()
    const stopRequested = useRef(false)
    const retryWaiting = useRef(false)
    const dialog = useRef<HTMLElement>(null)
    const closeButton = useRef<HTMLButtonElement>(null)
    const deleteDialog = useRef<HTMLDivElement>(null)
    const deleteTrigger = useRef<HTMLButtonElement | null>(null)
    const restoreDeleteFocus = useRef(false)
    const deleteTitleID = useId()
    const onChangedRef = useRef(onChanged)
    onChangedRef.current = onChanged
    const deleteTargetID = deleteTarget?.id

    useEffect(() => {
        if (!isOpen) return
        active.current = true
        pending.current = false; setBusy(false); setProgress(null); setUploadPosition('')
        stopRequested.current = false; retryWaiting.current = false
        setRetryWaitSeconds(null); setStopping(false)
        setError(''); setMessage(''); setFiles([]); setDeleteTarget(null); setDeleteError(''); setNewFolder(false)
        restoreDeleteFocus.current = false
        void loadCatalog(true).then(() => {
            if (!active.current) return
            const state = useAvatarCatalog.getState()
            setFolderID(state.folders.some(folder => folder.id === folderID) ? folderID : state.folders[0]?.id || 1)
            closeButton.current?.focus()
        })
        return () => {active.current = false; operation.current += 1; controller.current?.abort(); pending.current = false}
    }, [isOpen, loadCatalog]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const urls = isOpen ? files.slice(0, 6).map(file => URL.createObjectURL(file)) : []
        setPreviews(urls)
        return () => urls.forEach(url => URL.revokeObjectURL(url))
    }, [files, isOpen])

    useEffect(() => {
        if (!isOpen) return
        const preventFileNavigation = (event: DragEvent) => {
            const transfer = event.dataTransfer
            if (!transfer || (!Array.from(transfer.types || []).includes('Files') && !transfer.files?.length)) return
            event.preventDefault()
            transfer.dropEffect = 'none'
        }
        document.addEventListener('dragover', preventFileNavigation)
        document.addEventListener('drop', preventFileNavigation)
        return () => {
            document.removeEventListener('dragover', preventFileNavigation)
            document.removeEventListener('drop', preventFileNavigation)
        }
    }, [isOpen])

    useLayoutEffect(() => {
        if (!isOpen || deleteTargetID === undefined) return
        // Capture before Modal's passive effect locks scrolling. Its shared
        // implementation has no unmount cleanup, so restore it locally.
        const previousOverflow = document.body.style.overflow
        return () => {document.body.style.overflow = previousOverflow}
    }, [isOpen, deleteTargetID])

    useEffect(() => {
        if (deleteTargetID !== undefined) deleteDialog.current?.querySelector<HTMLButtonElement>('button[name="cancelAvatarDeletion"]')?.focus()
    }, [deleteTargetID])

    useEffect(() => {
        if (deleteTarget || busy || !restoreDeleteFocus.current) return
        restoreDeleteFocus.current = false
        if (deleteTrigger.current?.isConnected) deleteTrigger.current.focus()
        else closeButton.current?.focus()
    }, [deleteTarget, busy])

    const run = (permission: 'edit' | 'delete', work: (isCurrent: () => boolean) => Promise<void>) => {
        if (pending.current || !canManage || !(permission === 'edit' ? canEdit : canDelete) || isLoading || catalogError || !active.current) return
        pending.current = true; setBusy(true); setError(''); setMessage('')
        const version = ++operation.current
        const isCurrent = () => active.current && version === operation.current
        void work(isCurrent).catch(failure => {
            if (!isCurrent()) return
            reportUnexpectedClientError(failure, 'AvatarCatalog.mutation')
            setError('Операция не завершена. Обновите каталог и попробуйте ещё раз.')
        }).finally(() => {
            if (!isCurrent()) return
            pending.current = false; setBusy(false); setProgress(null); setUploadPosition('')
            retryWaiting.current = false; setRetryWaitSeconds(null); setStopping(false)
        })
    }
    const refresh = async (refreshProfile = false) => {
        await loadCatalog(true)
        if (refreshProfile) {
            const response = await useProfile.getState().actionRefreshAvatar()
            if (response.status !== 200 && active.current) setError('Каталог обновлён. Не удалось обновить аватар профиля — повторите проверку.')
        }
        if (active.current) {
            try { await onChangedRef.current?.() }
            catch (failure) {
                reportUnexpectedClientError(failure, 'AvatarCatalog.refreshOperators')
                if (active.current) setError('Изменения сохранены. Не удалось обновить список операторов.')
            }
        }
    }
    const selectFiles = (selected: File[]) => {
        if (!isOpen || !canManage || !canEdit || busy || isLoading || catalogError || deleteTarget || !folders.some(folder => folder.id === folderID)) return
        const all = Array.from(selected || [])
        const valid = all.filter(file => ALLOWED_TYPES.has(file.type) && file.size > 0 && file.size <= MAX_FILE_BYTES)
        setFiles(valid); setMessage(''); setError(valid.length === all.length ? '' : 'Часть файлов не добавлена: разрешены PNG, JPEG и WebP размером до 5 МиБ каждый.')
    }
    const upload = () => run('edit', async isCurrent => {
        if (!files.length || !folders.some(folder => folder.id === folderID)) return
        const queue = [...files], failed: File[] = []
        const destination = folderID
        controller.current = new AbortController()
        stopRequested.current = false; retryWaiting.current = false
        setRetryWaitSeconds(null); setStopping(false)
        let saved = 0, lastError = ''
        for (let index = 0; index < queue.length; index += 1) {
            if (!isCurrent()) return
            if (stopRequested.current) {failed.push(...queue.slice(index)); break}
            setUploadPosition(`${index + 1} из ${queue.length}: ${queue[index].name}`)
            setProgress(Math.round(index / queue.length * 100))
            let response
            try {
                response = await uploadCatalogAvatarWithRetry(queue[index], destination,
                    percent => {if (isCurrent()) setProgress(Math.round((index + percent / 100) / queue.length * 100))}, controller.current.signal,
                    seconds => {
                        if (!isCurrent()) return
                        retryWaiting.current = seconds !== null
                        setRetryWaitSeconds(seconds)
                        if (seconds !== null && stopRequested.current) controller.current?.abort()
                    })
            } catch (failure) {
                if (!isCurrent()) return
                reportUnexpectedClientError(failure, 'AvatarCatalog.upload')
                failed.push(...queue.slice(index)); lastError = 'Передача прервана. Уже загруженные файлы сохранены.'
                break
            }
            if (!isCurrent()) return
            if (response.status === 200 && response.data?.item) saved += 1
            else {
                failed.push(queue[index])
                if (!(stopRequested.current && response.status === 499)) lastError = response.message || 'Не удалось загрузить файл.'
                if (stopRequested.current || [401, 403, 429, 499].includes(response.status) || response.status >= 500) {failed.push(...queue.slice(index + 1)); break}
            }
            if (stopRequested.current) {failed.push(...queue.slice(index + 1)); break}
        }
        if (!isCurrent()) return
        setFiles(failed)
        setMessage(`${stopRequested.current ? 'Загрузка остановлена. ' : ''}Загружено: ${saved} из ${queue.length}.`)
        if (failed.length && lastError) setError(`${failed.length} файлов остались для повторной загрузки. ${lastError}`)
        await refresh()
    })
    const stopUpload = () => {
        stopRequested.current = true
        setStopping(true)
        // A rate-limited request has not saved a file. A running request must
        // finish before stopping, so a successful upload is never queued again.
        if (retryWaiting.current) controller.current?.abort()
    }
    const addFolder = () => run('edit', async isCurrent => {
        const name = folderName.trim()
        if (!name) return
        const response = await createAvatarFolder(name)
        if (!isCurrent()) return
        if (response.status !== 200 || !response.data?.folder) {setError(response.message || 'Не удалось создать папку.'); return}
        await loadCatalog(true)
        if (!isCurrent()) return
        setFolderID(response.data.folder.id); setFolderName(''); setNewFolder(false)
        setMessage(`Папка «${response.data.folder.name}» создана.`)
    })
    const remove = () => run('delete', async isCurrent => {
        if (!deleteTarget || ((deleteTarget.usageCount || 0) > 0 && !replacementID)) return
        const target = deleteTarget
        setDeleteError('')
        let response
        try {response = await deleteCatalogAvatar(target.id, replacementID)}
        catch (failure) {
            if (!isCurrent()) return
            reportUnexpectedClientError(failure, 'AvatarCatalog.delete')
            setDeleteError('Не удалось удалить аватар. Попробуйте ещё раз.')
            return
        }
        if (!isCurrent()) return
        if (response.status !== 200) {
            setDeleteError(response.message || 'Не удалось удалить аватар.')
            await loadCatalog(true)
            if (isCurrent()) setDeleteTarget(useAvatarCatalog.getState().items.find(item => item.id === target.id) || target)
            return
        }
        useAvatarCatalog.setState(state => ({items: state.items.filter(item => item.id !== target.id)}))
        restoreDeleteFocus.current = true
        setDeleteTarget(null); setReplacementID(undefined)
        setMessage(`Аватар №${target.id} удалён.${response.data?.reassignedCount ? ` Замена назначена операторам: ${response.data.reassignedCount}.` : ''}`)
        await refresh(true)
    })
    const cancelDelete = () => {
        if (pending.current) return
        restoreDeleteFocus.current = true
        setDeleteTarget(null); setReplacementID(undefined); setDeleteError('')
    }
    const keepDeleteFocus = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation()
            cancelDelete()
            return
        }
        // Enter on the replacement selector must never confirm deletion.
        if (event.key === 'Enter') {event.stopPropagation(); return}
        if (event.key !== 'Tab') return
        const fields = Array.from(deleteDialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled)') || [])
        if (!fields.length) {event.preventDefault(); return}
        const first = fields[0], last = fields[fields.length - 1]
        if (!fields.includes(document.activeElement as HTMLElement)) {event.preventDefault(); (event.shiftKey ? last : first).focus()}
        else if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last.focus()}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus()}
    }
    const keepFocus = (event: KeyboardEvent<HTMLElement>) => {
        if (deleteTarget) return
        if (event.key !== 'Tab') return
        const fields = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([tabindex="-1"]), select:not(:disabled), [tabindex="0"]') || [])
        if (!fields.length) {event.preventDefault(); return}
        if (event.shiftKey && document.activeElement === fields[0]) {event.preventDefault(); fields[fields.length - 1].focus()}
        else if (!event.shiftKey && document.activeElement === fields[fields.length - 1]) {event.preventDefault(); fields[0].focus()}
    }
    if (!isOpen) return null
    const catalogLocked = busy || isLoading || Boolean(catalogError)
    const locked = catalogLocked || Boolean(deleteTarget)
    const activeFolder = folders.find(folder => folder.id === folderID)
    return <><Drawer isOpen placement="right" size="min(1040px, 100vw)" maxWidth="100vw" isEsc={!busy && !deleteTarget} isWrapper={!busy && !deleteTarget} onCancel={() => {if (!pending.current && !deleteTarget) onClose()}}>
        <section ref={dialog} className={css.dialog} role="dialog" aria-modal="true" aria-hidden={deleteTarget ? true : undefined} aria-labelledby="avatar-catalog-title" aria-busy={busy || isLoading} onKeyDown={keepFocus}>
            <header className={css.header}><div><h4 id="avatar-catalog-title">Каталог аватаров</h4><p>Общие аватары операторов по папкам</p></div>
                <button ref={closeButton} className={css.close} type="button" disabled={busy || Boolean(deleteTarget)} onClick={onClose} aria-label="Закрыть каталог аватаров"><IconClearClose width="22px" height="22px" /></button></header>
            <div className={css.body}>
                <aside className={css.sidebar}><nav className={css.folders} aria-label="Папки аватаров">
                    {folders.map(folder => <button key={folder.id} type="button" aria-pressed={folderID === folder.id} disabled={locked}
                        onClick={() => {setFolderID(folder.id); setDeleteTarget(null)}}>{folder.name}<span>{items.filter(item => item.folderID === folder.id).length}</span></button>)}
                </nav>{canManage && canEdit && <Button size="sm" isDisabled={locked} onClick={() => setNewFolder(!newFolder)}>Новая папка</Button>}
                </aside>
                <main className={css.content}>
                    {isLoading && <p role="status">Загрузка каталога…</p>}
                    {(error || catalogError) && <p className={css.error} role="alert">{error || catalogError}</p>}
                    {message && <p className={css.success} role="status">{message}</p>}
                    <Button size="sm" isDisabled={busy || isLoading} onClick={() => {setError(''); void refresh(true).catch(failure => {
                        reportUnexpectedClientError(failure, 'AvatarCatalog.refresh')
                        if (active.current) setError('Не удалось обновить каталог и профиль. Попробуйте ещё раз.')
                    })}}>Обновить каталог</Button>
                    {!isLoading && (!canManage || (!canEdit && !canDelete)) && <p>Каталог доступен только для просмотра.</p>}
                    {canManage && canEdit && newFolder && <div className={css.upload}>
                        <label>Название новой папки<input autoFocus value={folderName} maxLength={80} disabled={locked} onChange={event => setFolderName(event.target.value)} /></label>
                        <div className={css.actions}><Button size="sm" isDisabled={locked || !folderName.trim()} onClick={addFolder}>Создать папку</Button>
                            <Button size="sm" variant="primaryDark" isDisabled={busy} onClick={() => setNewFolder(false)}>Отмена</Button></div>
                    </div>}
                    {canManage && canEdit && <div className={css.upload}>
                        <h5>Загрузка в папку «{activeFolder?.name || 'Выберите папку'}»</h5>
                        <p>PNG, JPEG или WebP, до 5 МиБ каждый. Можно выбрать несколько файлов. Изображения сохраняются до 512×512.</p>
                        <AvatarFileDropzone disabled={locked || !activeFolder} onFiles={selectFiles} />
                        {files.length > 0 && <><p>Выбрано файлов: {files.length}</p><div className={css.previews}>{previews.map((url, index) => <img key={url} src={url} alt={`Предпросмотр ${files[index]?.name || index + 1}`} />)}</div>
                            <ul className={css.file_list}>{files.map((file, index) => <li key={`${file.name}-${index}`}>{file.name}</li>)}</ul></>}
                        <Button size="sm" isDisabled={locked || !files.length || !activeFolder} onClick={upload}>Загрузить выбранные</Button>
                        {progress !== null && <div className={css.progress}>
                            <progress value={progress} max={100} aria-label="Загрузка аватаров" /><p>{uploadPosition} · {progress}%</p>
                            {retryWaitSeconds !== null && <p role="status">Сервер занят. Продолжим автоматически через {retryWaitSeconds} с.</p>}
                            {stopping && <p role="status">Останавливаем загрузку…</p>}
                            <div className={css.actions}><Button size="sm" variant="primaryDark" isDisabled={stopping} onClick={stopUpload}>Остановить загрузку</Button></div>
                        </div>}
                    </div>}
                    <h5 className={css.folder_title}>{activeFolder?.name || 'Аватары'}</h5>
                    <div className={css.grid}>{items.filter(item => item.folderID === folderID).map(item => <article key={item.id} className={css.item}>
                        <img src={getAvatarCatalogItemUrl(item)} alt={`Аватар ${item.id}`} loading="lazy" /><b>№{item.id}</b>
                        {canManage && <small>Используют: {item.usageCount || 0}</small>}
                        {canManage && canDelete && <Button size="xs" variant="primaryDark" isDisabled={locked || items.length < 2}
                            onClick={event => {deleteTrigger.current = event.currentTarget; setDeleteTarget(item); setReplacementID(undefined); setDeleteError('')}}>Удалить №{item.id}</Button>}
                    </article>)}</div>
                    {!isLoading && !catalogError && !items.some(item => item.folderID === folderID) && <p>В этой папке пока нет аватаров.</p>}
                </main>
            </div>
            <footer className={css.footer}>
                <p>Изменения каталога доступны всем операторам. Выбор личного аватара остаётся в профиле.</p>
            </footer>
        </section>
    </Drawer>
    {deleteTarget && <Modal isOpen isEsc={!busy} isWrapper={!busy} ariaLabelledBy={deleteTitleID}
        maxWidth="min(460px, calc(100vw - 24px))" className={css.delete_dialog} onCancel={cancelDelete}>
        <div ref={deleteDialog} className={css.confirm} onKeyDown={keepDeleteFocus} aria-busy={busy}>
            <div className={css.delete_header}>
                <img className={css.delete_preview} src={getAvatarCatalogItemUrl(deleteTarget)} alt={`Удаляемый аватар ${deleteTarget.id}`} />
                <div className={css.delete_heading}><span>Удаление из каталога</span><h4 id={deleteTitleID}>Удалить аватар №{deleteTarget.id}?</h4></div>
            </div>
            {(deleteTarget.usageCount || 0) > 0 && <div className={css.delete_replacement}><p>Его используют {deleteTarget.usageCount} операторов. Выберите аватар для замены — он будет назначен всем этим операторам.</p>
                <label>Заменить на<select aria-label="Аватар для замены" disabled={catalogLocked} value={replacementID || ''} onChange={event => setReplacementID(Number(event.target.value) || undefined)}>
                    <option value="">Выберите замену</option>{folders.map(folder => <optgroup key={folder.id} label={folder.name}>{items.filter(item => item.folderID === folder.id && item.id !== deleteTarget.id).map(item => <option key={item.id} value={item.id}>Аватар №{item.id}</option>)}</optgroup>)}
                </select></label></div>}
            {(deleteError || catalogError) && <p className={css.error} role="alert">{deleteError || catalogError}</p>}
            <div className={css.delete_actions}>
                <Button name="cancelAvatarDeletion" size="sm" variant="primaryDark" width="100%" isDisabled={busy} onClick={cancelDelete}>Отмена</Button>
                <Button size="sm" width="100%" isLoading={busy} isDisabled={!canManage || !canDelete || catalogLocked || !items.some(item => item.id === deleteTarget.id)
                    || ((deleteTarget.usageCount || 0) > 0 && !items.some(item => item.id === replacementID && item.id !== deleteTarget.id))}
                    onClick={remove}>Удалить аватар</Button>
            </div>
        </div>
    </Modal>}
    </>
}
